import {
  QuerySnapshot,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { firebaseDb } from './init'
import {
  ICreateExamPaper,
  ICreateWorkSolution,
  IExamPaper,
  IExamPaperRaw,
  IGenericError,
  IWorkedSolution,
  IWorkedSolutionRaw,
} from '../types'

const examPapersCollectionName = 'examPapers'
const workSolutionsSubCollectionName = 'workSolutions'

const examPapersCollection = collection(firebaseDb, examPapersCollectionName)

const examPaperRef = (id: string) =>
  doc(firebaseDb, examPapersCollectionName, id)

export async function getExamPaperById(
  examPaperId: string,
): Promise<IExamPaper> {
  try {
    const examPaperDoc = examPaperRef(examPaperId)
    const examPaperSnapshot = await getDoc(examPaperDoc)
    const examPaperData = examPaperSnapshot.data() as IExamPaperRaw

    return { ...examPaperData, id: examPaperSnapshot.id }
  } catch (error) {
    console.error(
      `error while trying to get exam paper by id: ${examPaperId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get exam paper by id`,
    )
  }
}

export async function getExamPapersWorkedSolutions(
  examPaperId: string,
): Promise<IWorkedSolution[]> {
  try {
    const variantsSubCollectionRef = collection(
      firebaseDb,
      examPapersCollectionName,
      examPaperId,
      workSolutionsSubCollectionName,
    )

    const querySnapshot = (await getDocs(
      variantsSubCollectionRef,
    )) as QuerySnapshot<IWorkedSolutionRaw>

    const workSolutions: IWorkedSolution[] = []

    querySnapshot.docs.forEach((doc) => {
      const workSolution: IWorkedSolution = {
        id: doc.id,
        ...doc.data(),
      }

      workSolutions.push(workSolution)
    })

    return workSolutions
  } catch (error) {
    console.error(
      `error while trying to get exam paper work solutions sub collection documents from db, examPaperId: ${examPaperId} :>> `,
      (error as IGenericError)?.message || error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to get exam paper work solutions sub collection documents`,
    )
  }
}

export async function setExamPaperById(
  id: string,
  examPaper: ICreateExamPaper,
): Promise<void> {
  try {
    await setDoc(examPaperRef(id), {
      ...examPaper,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error(
      `error while trying to set exam paper by id: ${id} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to set exam paper by id`,
    )
  }
}

export async function setWorkedSolutionsToExamPaper(
  examPaperId: string,
  solutionId: string,
  workedSolution: ICreateWorkSolution,
): Promise<void> {
  try {
    const paperRef = doc(firebaseDb, examPapersCollectionName, examPaperId)

    const paperSnap = await getDoc(paperRef)

    if (!paperSnap.exists()) {
      throw new Error(
        `Set worked solutions to exam paper failed. Exam Paper with id: ${examPaperId} not found`,
      )
    }

    const workSolutionRef = doc(
      paperRef,
      workSolutionsSubCollectionName,
      solutionId,
    )

    await setDoc(workSolutionRef, {
      ...workedSolution,
      createdAt: serverTimestamp(),
    })
  } catch (error) {
    console.error(
      `error while trying to set worked solution to exam paper sub-collection by examPaperId: ${examPaperId}, solutionId: ${solutionId} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to set worked solution to exam paper sub-collection by examPaperId`,
    )
  }
}

export async function updateExamPaperById(
  id: string,
  examPaper: Partial<IExamPaperRaw>,
): Promise<void> {
  try {
    await updateDoc(examPaperRef(id), {
      ...examPaper,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error(
      `error while trying to update exam paper by id: ${id} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update exam paper by id`,
    )
  }
}

export async function updateWorkedSolutionById(
  examPaperId: string,
  solutionId: string,
  workedSolution: Partial<IWorkedSolutionRaw>,
): Promise<void> {
  try {
    const paperRef = doc(firebaseDb, examPapersCollectionName, examPaperId)

    const paperSnap = await getDoc(paperRef)

    if (!paperSnap.exists()) {
      throw new Error(
        `Update worked solution for exam paper failed. Exam Paper with id: ${examPaperId} not found`,
      )
    }

    const workSolutionRef = doc(
      paperRef,
      workSolutionsSubCollectionName,
      solutionId,
    )

    await updateDoc(workSolutionRef, {
      ...workedSolution,
      updatedAt: serverTimestamp(),
    })
  } catch (error) {
    console.error(
      `error while trying to update worked solution from exam paper sub-collection by examPaperId: ${examPaperId}, solutionId: ${solutionId} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to update worked solution for exam paper sub-collection by examPaperId`,
    )
  }
}

export async function removeAllDocsFromWorkedSolutionsSubCollectionByPaperId(
  examPaperId: string,
): Promise<void> {
  try {
    const paperRef = doc(firebaseDb, examPapersCollectionName, examPaperId)

    const paperSnap = await getDoc(paperRef)

    if (!paperSnap.exists()) {
      throw new Error(
        `Update worked solution for exam paper failed. Exam Paper with id: ${examPaperId} not found`,
      )
    }

    const workSolutionsRef = collection(
      paperRef,
      workSolutionsSubCollectionName,
    )
    const snapshot = await getDocs(workSolutionsRef)

    if (snapshot.empty) {
      console.debug(
        `No documents found in workSolutions for examPaperId: ${examPaperId} while trying to remove all docs from workSolutions sub-collection`,
      )
      return
    }

    await Promise.all(snapshot.docs.map((docSnap) => deleteDoc(docSnap.ref)))

    console.debug(
      `All documents removed from workSolutions sub-collection for examPaper with id: ${examPaperId}. documents count: ${snapshot.docs.length}`,
    )
  } catch (error) {
    console.error(
      `error while trying to remove all docs from workSolutions sub-collection for examPaper with id: ${examPaperId} :>> `,
      error,
    )

    throw new Error(
      (error as IGenericError)?.message ||
        `Unexpected error while trying to remove all docs from workSolutions sub-collection for examPaper`,
    )
  }
}
